import { getSocialProviderText } from "@faire/deprecated-retailer-visitor-shared/lib/ssoUtils";
import { useSSOStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { Core } from "@faire/slate/foundation/color";
import { trackSigninSsoClick } from "@faire/web-api--source/events/signin/click/sso";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Apple } from "@faire/web/slate/icons/external/Apple";
import { Google } from "@faire/web/slate/icons/external/Google";
import { getSpacing } from "@faire/web/slate/spacing";
import { GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, type JSX } from "react";
import styled from "styled-components";

interface IProps {
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  socialProvider: SocialSignOnProvider;
  isOnSignIn?: boolean;
  sourceId: string;
}

export const BaseSocialLoginButton: FC<IProps> = (props) => {
  const { disabled, loading, socialProvider, onClick, isOnSignIn, sourceId } =
    props;
  const { recordSSOButtonClick } = useSSOStore();
  let icon: JSX.Element | null = null;
  switch (socialProvider) {
    case SocialSignOnProvider.GOOGLE:
      icon = <StyledGoogleIcon fontSize="20px" $disabled={disabled} />;
      break;
    case SocialSignOnProvider.OKTA:
      // TODO (FD-227733): Change to StyledOktaIcon once added
      icon = (
        <img
          className="oktaIcon"
          title="Okta logo"
          src="https://cdn.faire.com/fastly/467172fbeba306a9dc5696feb65261ea823ecd289aca5df68b8ffca1d1441449.png"
          style={{
            height: "40px",
            width: "40px",
          }}
        ></img>
      );
      break;
    case SocialSignOnProvider.APPLE:
      icon = <StyledAppleIcon fontSize="20px" $disabled={disabled} />;
      break;
    default:
      break;
  }

  return (
    <StyledButton
      disabled={disabled}
      loading={loading}
      variant="secondary"
      onClick={() => {
        recordSSOButtonClick();
        trackSigninSsoClick(socialProvider, sourceId);
        onClick?.();
      }}
    >
      {icon}
      <GrowingSpacer />
      {isOnSignIn ? (
        <SignInCopy socialProvider={socialProvider} />
      ) : (
        <SignupCopy socialProvider={socialProvider} />
      )}
      <GrowingSpacer />
      <Spacer width="3x" />
    </StyledButton>
  );
};

const SignInCopy: React.FC<{
  socialProvider: SocialSignOnProvider;
}> = ({ socialProvider }) => {
  return (
    <StrictLocalMsg
      id="hFeXVj"
      defaultMessage="Sign in with {socialProviderCompanyName}"
      description={{
        text: "Sign up message for {socialProviderCompanyName}",
        img: "https://cdn.faire.com/fastly/79070ba0dfb88343d9f4ce5033205c464e061e7d992b1fe13966b177d0811339.png",
      }}
      values={{
        socialProviderCompanyName: getSocialProviderText(socialProvider),
      }}
    />
  );
};

const SignupCopy: React.FC<{
  socialProvider: SocialSignOnProvider;
}> = ({ socialProvider }) => {
  return (
    <StrictLocalMsg
      id="zaAT+X"
      defaultMessage="Sign up with {socialProviderCompanyName}"
      description={{
        text: "Sign up message for {socialProviderCompanyName}",
        img: "https://cdn.faire.com/fastly/061a4ac10dc8675b892156ecd994b57f092ec2ea23f67943a63af308b0dad885.png",
      }}
      values={{
        socialProviderCompanyName: getSocialProviderText(socialProvider),
      }}
    />
  );
};

const StyledButton = styled(Button)`
  border: 1px solid ${Core.border.muted};
  padding: ${getSpacing(0, "3x")};
  width: 100%;

  &:hover {
    img.oktaIcon {
      filter: invert(1);
    }
  }
`;

const StyledAppleIcon = styled(Apple)<{ $disabled?: boolean }>`
  ${(props) => (props.$disabled ? "opacity: 0.5;" : "")}
`;

const StyledGoogleIcon = styled(Google)<{ $disabled?: boolean }>`
  ${(props) => (props.$disabled ? "opacity: 0.5;" : "")}
`;
